import Component from "../../component";
import {CollectionInsert} from "./components/collection_insert";
import {GridItem} from "./grid_item";
import {GridItemsModel} from "./grid_items_model";
import {interweave, shuffle} from "../../helpers/array";

export const ProductCollectionConfig = {
    signature: 'productCollection',
    baseHtmlClass: 'collection',
    selector: '.collection',
    initedHtmlClassModifier: 'inited',
    datasetConfigKey: 'product_collection_config_data',

    collectionGridSelector: '.collection__items',
    collectionItemSelector: '.collection__items__item',
    collectionItemBaseHtmlClass: 'collection__items__item',

};

// The ProductCollection component represents a series of products and manages them in a few ways:
//
// 1. Interweaves a bunch of CollectionInsert components into the collection so that additional content is presented alongside the products (eg. USP content). These are "Prefiller" inserts.
//
// 2. Fills any gaps in the presented grid of products (and prefiller inserts) so that the grid is always full and without holes.
// This is managed even as the grid changes (columns are added/removed). In order to do this we must model the grid in JS (as a matrix) and it's behavior to match the CSS rules.
// This is necessary because there is no (simple) way to find a hole in CSS grid by interrogating the "on-page" grid elements.
//
export class ProductCollection extends Component {

    static applyToAll() {
        document.querySelectorAll(ProductCollectionConfig.selector).forEach((element) => {
            if (!ProductCollection.isElementComponentized(element, ProductCollectionConfig.signature)) new ProductCollection(element);
        });
    }

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: ProductCollectionConfig
        });

        this.setDatasetConfig(ProductCollectionConfig.datasetConfigKey);
        this.handle = this.getDatasetConfigValue('handle');


        // We have CollectionInsert components that can be inserted into the product collection grid. These contain additional content, not product content.
        // For example a CollectionInsert component might have content related to a email newsletter signup.
        //
        // "Prefillers" are CollectionInsert components that will get interwoven into the existing collection items.
        // This means that there are always a few shown (even when the grid has no holes to fill).
        // This happens prior to the grid being modeled.
        //
        // Then once we've modeled the grid we will insert "Fillers" CollectionInsert components to plug any holes in the grid model.
        // This is repeated whenever the grid changes (eg. on resize) to fill any gaps.

        // Construct all our CollectionInsert components…
        let allCollectionInsertElements = getCollectionInsertElements(this.handle);
        this.collectionInserts = CollectionInsert.constructAll(allCollectionInsertElements);

        // Divvy up the inserts into "Prefillers" and "Fillers".
        // Prefillers are always presented in the grid. Fillers are used inserted to fill holes in the grid.
        this.prefillerCollectionInserts = this.getPrefillerCollectionInserts();
        this.fillerCollectionInserts = this.getFillerCollectionInserts();

        // Get a reference to the actual grid element…
        this.collectionGridElement = this.element.querySelector(ProductCollectionConfig.collectionGridSelector);
        if (!this.collectionGridElement) return;

        // Append the prefiller inserts to the grid as grid items before we model grid in JS. This way the prefiller items are an integral part of the grid and always show.
        this.appendPrefillerInserts();

        // Get the dimensions of our grid with prefiller inserts included.
        const dimensions = calcGridDimensions(this.collectionGridElement);
        this.gridColumnsCount = dimensions['cols'];
        this.gridRowsCount = dimensions['rows'];

        // Get our grid item elements including the interwoven prefiller inserts.
        // This is a reference to grid items prior to adding filler items to the grid.
        this.collectionItemElements = this.collectionGridElement.querySelectorAll(ProductCollectionConfig.collectionItemSelector);

        // This is where the modelling of the grid starts.
        // The model is responsible for filling any holes with filler inserts within the model itself (not the DOM).
        // This ProductCollection component is responsible for rendering grid elements and fillers to the DOM (see `render` method).
        this.gridItemsModel = this.constructGridItemsModel();

        // The ProductCollection grid is watched for resize events.
        // The column count is passed to the grid model and if it changes then the model is updated and this ProductCollection rendered to fill any holes.
        this.isResizeTicking = false;
        const resizeObserver = new ResizeObserver(this.resizeHandler.bind(this));
        resizeObserver.observe(this.element);

        this.render();
    }

    // This resize handler throttles the calls to `handleResize` which actually does the work.
    resizeHandler() {
        if (!this.isResizeTicking) {
            window.requestAnimationFrame(() => {
                this.handleResize();
                this.isResizeTicking = false;
            });
            this.isResizeTicking = true;
        }
    };

    handleResize() {
        const newGridColumnsCount = this.calcGridColumnsCount();
        if (this.gridColumnsCount === newGridColumnsCount) return; // Return early when column count hasn't changed.

        this.gridColumnsCount = newGridColumnsCount;
        this.gridItemsModel.setColumnCount(this.gridColumnsCount);
        this.render();
    }

    calcGridColumnsCount() {
        return calcGridDimensions(this.collectionGridElement)['cols'];
    }

    // Construct a model of the grid and grid items by interrogating the elements on the page.
    // The model receives the filler inserts also because it's responsible for filling holes.
    constructGridItemsModel() {
        let items = [];
        this.collectionItemElements.forEach((element, index) => {
            let prominence = element.dataset['grid_item_prominence'];
            if (!prominence || prominence === '') prominence = "1";
            items.push(new GridItem(index, prominence, {'element': element}))
        });
        return new GridItemsModel(items, {
            'columnCount': this.gridColumnsCount,
            'fillerCollectionInserts': this.fillerCollectionInserts
        });
    }

    appendPrefillerInserts() {
        let prefillerElements = this.prefillerCollectionInserts.map((insert) => insert.element);
        let firstPrefillerElement = prefillerElements.shift();

        // Query returns a Nodeslist, when we need an Array for the `interweave` function…
        let nodesList = this.collectionGridElement.querySelectorAll(ProductCollectionConfig.collectionItemSelector);
        let existingElements = Array.prototype.slice.call(nodesList);

        // Insert the first prefiller at index
        const index = 3
        existingElements.splice(index, 0, firstPrefillerElement);

        const mergedElements = interweave(existingElements, prefillerElements);

        mergedElements.forEach((element) => {
            this.collectionGridElement.appendChild(element);
        });
    }

    render() {
        this.collectionGridElement.textContent = ''; // Apparently the fastest way to clear the child items from the grid (https://stackoverflow.com/a/3955238/19588)
        let itemElements = this.gridItemsModel.getMatrixElements();
        itemElements.forEach((element) => {
            this.collectionGridElement.appendChild(element);
        });
    }

    getPrefillerCollectionInserts() {
        const collectionInserts = this.collectionInserts.slice(0, 0); // The first 0 become prefillers. Zero because prefillers are currently disabled.
        // Mark the prefiller elements with a classname
        collectionInserts.forEach((collectionInsert) => {
            collectionInsert.element.classList.add(`${ProductCollectionConfig.collectionItemBaseHtmlClass}--prefiller`)
        });
        return collectionInserts;
    }

    getFillerCollectionInserts() {
        const collectionInserts = this.collectionInserts.slice(3); // From insert 3 onwards become fillers.
        // Mark the filler elements with a classname
        collectionInserts.forEach((collectionInsert) => {
            collectionInsert.element.classList.add(`${ProductCollectionConfig.collectionItemBaseHtmlClass}--filler`)
        });
        return collectionInserts;
    }
}

function getCollectionInsertElements(handle) {
    let elements = [];
    let insertsElement = document.querySelector(`#${handle}_inserts.inserts`);
    if (insertsElement) {
        insertsElement.querySelectorAll('.insert_item').forEach((element) => elements.push(element))
        insertsElement.parentElement.removeChild(insertsElement); // Remove coz we no longer need this element in the DOM.
    }

    // Add the appropriate HTML class so that the inserts will look like their peers once inserted in to the grid.
    elements.forEach((element) => {
        element.classList.add(ProductCollectionConfig.collectionItemBaseHtmlClass, `${ProductCollectionConfig.collectionItemBaseHtmlClass}--insert`);
    });

    return shuffle(elements);
}

// https://stackoverflow.com/a/58393617/19588
function calcGridDimensions(gridElement) {
    let columnCount = null;
    let rowCount = null;
    try {
        const gridComputedStyle = window.getComputedStyle(gridElement);
        columnCount = gridComputedStyle.getPropertyValue("grid-template-columns").replace(' 0px', '').split(" ").length;
        rowCount = gridComputedStyle.getPropertyValue("grid-template-rows").replace(' 0px', '').split(" ").length;
    } catch {
        console.warn('[calcGridDimensions] Could not get grid dimensions from passed in element', gridElement);
    }
    return {'cols': columnCount, 'rows': rowCount};
}












